import React, { useState } from "react";
import { useFormikContext } from "formik";
import { getIconForUID } from "components/FormerEditor/utils";
import { getEntitlementConfigTemplate } from "utils/entitlements";
import classnames from "classnames";
import { COLORS } from "utils/styles";
import Modal from "components/Modal";
import cloneDeep from "lodash/cloneDeep";
import HoverIconButton from "components/HoverIconButton";
import CellEntitlementForm from "../CellEntitlementForm";
import Tooltip from "components/Tooltip";
import Analytics, {
  TOGGLE_PRODUCT_FEATURE_CONFIG,
  SHOW_PRODUCT_FEATURE_CONFIG_SETTINGS
} from "utils/analytics";
import get from "lodash/get";
import set from "lodash/set";

const Icon = getIconForUID("checkmark");
const ENTITLEMENTS = "entitlements";
const EMPTY = "";

const CellEntitlement = ({
  tracking,
  feature,
  featureGroup,
  product,
  tdWidth,
  snapshot,
  provided,
  productIndex,
  activeIndex
}) => {
  const isActiveIndex = activeIndex === productIndex;
  const [showModal, setShowModal] = useState(false);
  const [hoverCell, setHoverCell] = useState(false);
  const [hoverValue, setHoverValue] = useState(false);

  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const entitlementKey = `${feature.uuid}.${product.id}`;
  const fieldKey = `${ENTITLEMENTS}.${entitlementKey}`;

  const isTouched = Boolean(get(touched, fieldKey));
  const entitlement = get(values, fieldKey);

  const deselectedIcon = <Icon size={24} color={COLORS.deselected} />;

  let content = EMPTY;
  if (entitlement) {
    if (entitlement.enabled) {
      const value = entitlement.value || <Icon size={24} color={COLORS.blue} />;
      const tipId = `${product.id}.${featureGroup.uuid}.${feature.uuid}`;

      content = entitlement.tooltip ? (
        <Tooltip id={tipId} tooltip={entitlement.tooltip}>
          {value}
        </Tooltip>
      ) : (
        value
      );
    } else if (hoverCell) {
      content = entitlement.value ? (
        <div className="f5 o-40">{entitlement.value}</div>
      ) : (
        deselectedIcon
      );
    }
  } else if (hoverCell) {
    content = deselectedIcon;
  }

  /**
   * Turn the feature on / off for a product
   */
  const toggleValue = () => {
    Analytics.track(TOGGLE_PRODUCT_FEATURE_CONFIG, tracking);
    const updatedEntitlements = cloneDeep(values.entitlements);
    let updatedEntitlement = entitlement && cloneDeep(entitlement);

    if (updatedEntitlement) {
      updatedEntitlement.enabled = !updatedEntitlement.enabled;
    } else {
      updatedEntitlement = getEntitlementConfigTemplate({
        enabled: true
      });
    }
    set(updatedEntitlements, entitlementKey, updatedEntitlement);

    setFieldValue(ENTITLEMENTS, updatedEntitlements);
    if (!isTouched) {
      setFieldTouched(fieldKey, true);
    }
  };

  /**
   * Replace the entitlement with the whole set of updates
   */
  const updateEntitlement = (updates) => {
    const updatedEntitlements = cloneDeep(values.entitlements);
    set(updatedEntitlements, entitlementKey, updates);

    setFieldValue(ENTITLEMENTS, updatedEntitlements);
    if (!isTouched) {
      setFieldTouched(fieldKey, true);
    }
    setShowModal(false);
  };

  const valueProps = {
    className: classnames("f5 pointer w-100", {
      "dib": !snapshot.isDragging,
      "flex center h-100 items-center justify-center": snapshot.isDragging
    }),
    onMouseEnter: () => !hoverValue && setHoverValue(true),
    onMouseLeave: () => hoverValue && setHoverValue(false)
  };

  /**
   * Desktop
   * - Dragging render all cells
   * Mobile
   * - Dragging: Only render the active config cell
   */
  const containerProps = {
    className: classnames("tc bb br hairline-1 relative dtc-ns", {
      "bg-white": !hoverCell && !snapshot.isDragging,
      "bg-content--secondary": hoverCell && !snapshot.isDragging,
      "ba fl": snapshot.isDragging,
      "dn": !isActiveIndex
    }),
    onMouseEnter: () => !hoverCell && setHoverCell(true),
    onMouseLeave: () => hoverCell && setHoverCell(false),
    style: { width: `${tdWidth}%` }
  };
  /**
   * Apply styles to the element when in an active dragging state
   */
  if (snapshot.isDragging) {
    containerProps.style.height = provided.draggableProps.style.height;
    containerProps.style["borderColor"] = COLORS.blue;
    containerProps.style["borderLeft"] = 0;
    containerProps.style["backgroundColor"] = COLORS.emphasis;
  }

  let componentContent;
  const settingsButton = (
    <HoverIconButton
      type="gear"
      tooltip="Settings"
      onClick={() => {
        Analytics.track(SHOW_PRODUCT_FEATURE_CONFIG_SETTINGS, tracking);
        setShowModal(!showModal);
        setHoverCell(false);
        setHoverValue(false);
      }}
    />
  );
  if (!snapshot.isDragging) {
    componentContent = (
      <div className="flex flex-row items-center justify-center">
        <div className="w-10 flex">
          {hoverCell && <div className="dn db-ns">{settingsButton}</div>}
          <div className="db dn-ns">{settingsButton}</div>
        </div>
        <div
          onClick={toggleValue}
          className="o-100-ns f6 f5-ns pv2 w-80 items-center justify-center h-100 flex pointer"
        >
          {content}
        </div>
        <div
          onClick={toggleValue}
          className="w-10 flex bg-transparent pointer"
        >{` `}</div>
      </div>
    );
  } else {
    componentContent = (
      <div className="pa2 flex items-center justify-center">{content}</div>
    );
  }

  return (
    <td {...containerProps}>
      <div {...valueProps}>{componentContent}</div>
      {showModal && (
        <Modal
          key={`${feature.id}.${product.id}`}
          showModal={showModal}
          onClose={() => setShowModal(false)}
        >
          <CellEntitlementForm
            feature={feature}
            product={product}
            entitlement={entitlement}
            setShowModal={setShowModal}
            updateEntitlement={updateEntitlement}
            featureGroup={featureGroup}
          />
        </Modal>
      )}
    </td>
  );
};

export default CellEntitlement;
